import {
  Box,
  Tag,
  type TagVariant,
  type HighlightIconVariant,
  type IconName,
} from '@dev-spendesk/grapes';
import { type ReactNode } from 'react';

import { type TGlobalFunctionTyped } from 'common/hooks/useTranslation';
import { defaultIconCollection } from 'src/core/common/components/IconPicker/IconPicker';
import { type CurrenciesKey } from 'src/core/config/money';
import { type Company } from 'src/core/modules/app/hooks/useCompany';
import {
  type EnrichedValueFilter,
  type EnrichedPayableRule,
  type EnrichedCustomFieldFilter,
} from 'src/core/modules/payable';
import { displayMoney } from 'src/core/utils/money';
import { logger } from 'src/utils/datadog-log-wrapper';

import { getReadablePayableType } from './models/payableRules';

export type TagCollection = {
  tags: { textContent: string; isArchived: boolean }[];
  isArchived: boolean; // Flag to check if the custom fields is deleted
  isProblematic: boolean; // Flag to check if any value in the custom fields is problematic
};

export const getIconVariantFromName = (
  iconName: EnrichedPayableRule['icon'],
): HighlightIconVariant => {
  const selectedIcon = defaultIconCollection.find(
    (icon) => icon.name === iconName,
  );
  if (selectedIcon) {
    return selectedIcon.color;
  }
  logger.warn('[Payable Rules]: unhandled icon name', {
    iconName,
    scope: 'bookkeep-payable-rules',
    team: 'finance-accountant',
  });
  return 'info';
};

export const getGrossAmountTag = ({
  condition,
  currency,
}: {
  condition: Extract<
    EnrichedPayableRule['conditions'][number],
    { field: 'grossAmount' }
  >;
  currency: CurrenciesKey;
}) => {
  let tagContent = '';

  switch (condition.operator) {
    case 'between':
      tagContent = `${displayMoney({
        value: condition.value.from,
        currency,
        minimumFractionDigits: 0,
      })} < x < ${displayMoney({
        value: condition.value.to,
        currency,
        minimumFractionDigits: 0,
      })}`;
      break;
    case 'notBetween':
      tagContent = `x < ${displayMoney({
        value: condition.value.from,
        currency,
        minimumFractionDigits: 0,
      })} < ${displayMoney({
        value: condition.value.to,
        currency,
        minimumFractionDigits: 0,
      })} < x`;
      break;
    case '>':
    case '<':
    case '=':
    case '!=':
    case '>=':
    case '<=':
      tagContent = `${condition.operator}${displayMoney({
        value: condition.value,
        currency,
        minimumFractionDigits: 0,
      })}`;
      break;
    default:
      tagContent = 'Unsupported operator';
      logger.warn('Payable rules table: unsupported operator', {
        // @ts-expect-error it should not happen, type is never :) will remove later on if this never pops up
        operator: condition.operator,
        scope: 'accounting-payable-rules',
        team: 'finance-accountant',
      });
      break;
  }

  return tagContent;
};

export const getCustomFieldTag = ({
  condition,
  translator,
}: {
  condition: EnrichedCustomFieldFilter;
  translator: TGlobalFunctionTyped;
}): TagCollection => {
  const customField =
    'customField' in condition.value ? condition.value.customField : undefined;
  const tagCollection: TagCollection = {
    tags: [],
    isProblematic: false, // Flag to check if any value in the custom fields is problematic
    isArchived: false, // Flag to check if the custom fields is deleted
  };
  if (!customField) {
    return tagCollection;
  }

  tagCollection.isArchived = !!customField.archiveDate;
  if (Array.isArray(condition.value.value)) {
    const customFieldValues = condition.value.value.map((customFieldValue) => {
      const isArchived =
        typeof customFieldValue === 'object' && !!customFieldValue?.archiveDate;
      if (isArchived) {
        tagCollection.isProblematic = true;
      }
      const customFieldValueName =
        typeof customFieldValue === 'object'
          ? customFieldValue.name
          : undefined;
      if (customField.type === 'Boolean') {
        return {
          textContent: `${customField.name}: ${customFieldValueName}`,
          isArchived,
        };
      }
      if (!customFieldValueName) {
        return {
          textContent: '',
          isArchived,
        };
      }
      const customFieldValueContent =
        condition.operator === '='
          ? customFieldValueName
          : `≠ ${customFieldValueName}`;
      return {
        textContent: customFieldValueContent,
        isArchived,
      };
    });
    tagCollection.tags = customFieldValues.filter(
      (value) => typeof value.textContent === 'string' && value.textContent,
    ) as { textContent: string; isArchived: boolean }[];
  } else if (condition.value.value === null) {
    const textContent =
      condition.operator === '='
        ? translator(
            'bookkeep.integrations.settings.payableRules.emptyCustomFieldValue',
            { customField: customField.name },
          )
        : translator(
            'bookkeep.integrations.settings.payableRules.anyCustomFieldValue',
            { customField: customField.name },
          );
    tagCollection.tags.push({
      textContent,
      isArchived: false,
    });
  } else if (condition.value.value === 'any') {
    const textContent = getCustomFieldTag({
      condition: {
        field: condition.field,
        operator: negateOperator(condition.operator),
        value: { customField, value: null },
      },
      translator,
    });
    tagCollection.tags.push(...textContent.tags);
  }

  return tagCollection;
};

export const getPayableTypeTag = ({
  condition,
  translator,
}: {
  condition: Extract<
    EnrichedPayableRule['conditions'][number],
    { field: 'payableType' }
  >;
  translator: TGlobalFunctionTyped;
}) => {
  let tagContent = '';
  if (Array.isArray(condition.value)) {
    const payableTypes: string[] = [];
    condition.value.forEach((conditionValue) => {
      const readableType = getReadablePayableType(conditionValue);
      if (readableType) {
        const prefix = condition.operator === '=' ? '' : '≠ ';
        payableTypes.push(`${prefix}${translator(readableType)}`);
      }
    });
    tagContent = payableTypes.join('/');
  } else if (condition.value === null) {
    tagContent =
      condition.operator === '='
        ? translator(
            'bookkeep.integrations.settings.payableRules.emptyPayableType',
          )
        : translator(
            'bookkeep.integrations.settings.payableRules.anyPayableType',
          );
  } else if (condition.value === 'any') {
    tagContent = getPayableTypeTag({
      condition: {
        field: condition.field,
        operator: negateOperator(condition.operator),
        value: null,
      },
      translator,
    });
  }
  return tagContent;
};

export const getRequestorTag = ({
  condition,
  translator,
}: {
  condition: EnrichedValueFilter<'requestor'>;
  translator: TGlobalFunctionTyped;
}): TagCollection => {
  const tagCollection: TagCollection = {
    tags: [],
    isProblematic: false,
    isArchived: false,
  };

  if (Array.isArray(condition.value)) {
    tagCollection.tags = condition.value
      .map((requestor) => {
        if (typeof requestor === 'string') {
          return {
            textContent: '',
            isArchived: false,
          };
        }
        return {
          textContent:
            condition.operator === '='
              ? requestor.fullname
              : `≠ ${requestor.fullname}`,
          isArchived: requestor.isDeleted ?? false,
        };
      })
      .filter((tag) => Boolean(tag.textContent));

    tagCollection.isArchived = tagCollection.tags.every(
      (tag) => tag.isArchived,
    );
    tagCollection.isProblematic = tagCollection.tags.some(
      (tag) => tag.isArchived,
    );
  } else if (condition.value === null) {
    tagCollection.tags.push({
      textContent:
        condition.operator === '='
          ? translator(
              'bookkeep.integrations.settings.payableRules.emptyRequestor',
            )
          : translator(
              'bookkeep.integrations.settings.payableRules.anyRequestor',
            ),
      isArchived: false,
    });
  } else if (condition.value === 'any') {
    return getRequestorTag({
      condition: {
        field: condition.field,
        operator: negateOperator(condition.operator),
        value: null,
      },
      translator,
    });
  }
  return tagCollection;
};

export const getCostCenterTag = ({
  condition,
  translator,
}: {
  condition: EnrichedValueFilter<'costCenter'>;
  translator: TGlobalFunctionTyped;
}): TagCollection => {
  const tagCollection: TagCollection = {
    tags: [],
    isProblematic: false,
    isArchived: false,
  };

  if (Array.isArray(condition.value)) {
    tagCollection.tags = condition.value
      .map((costCenter) => {
        if (typeof costCenter === 'string') {
          return {
            textContent: '',
            isArchived: false,
          };
        }
        return {
          textContent:
            condition.operator === '='
              ? costCenter.name
              : `≠ ${costCenter.name}`,
          isArchived: costCenter.isDeleted ?? false,
        };
      })
      .filter((tag) => Boolean(tag.textContent));

    tagCollection.isArchived = tagCollection.tags.every(
      (tag) => tag.isArchived,
    );
    tagCollection.isProblematic = tagCollection.tags.some(
      (tag) => tag.isArchived,
    );
  } else if (condition.value === null) {
    tagCollection.tags.push({
      textContent:
        condition.operator === '='
          ? translator(
              'bookkeep.integrations.settings.payableRules.emptyCostCenter',
            )
          : translator(
              'bookkeep.integrations.settings.payableRules.anyCostCenter',
            ),
      isArchived: false,
    });
  } else if (condition.value === 'any') {
    return getCostCenterTag({
      condition: {
        field: condition.field,
        operator: negateOperator(condition.operator),
        value: null,
      },
      translator,
    });
  }

  return tagCollection;
};

export const getSupplierTag = ({
  condition,
  translator,
}: {
  condition: EnrichedValueFilter<'supplier'>;
  translator: TGlobalFunctionTyped;
}): TagCollection => {
  const tagCollection: TagCollection = {
    tags: [],
    isProblematic: false,
    isArchived: false,
  };

  if (Array.isArray(condition.value)) {
    tagCollection.tags = condition.value
      .map((supplier) => {
        if (typeof supplier === 'string') {
          return {
            textContent:
              condition.operator === '=' ? supplier : `≠ ${supplier}`,
            isArchived: false,
          };
        }
        return {
          textContent:
            condition.operator === '=' ? supplier.name : `≠ ${supplier.name}`,
          isArchived: supplier.isArchived,
        };
      })
      .filter((tag) => Boolean(tag.textContent));

    tagCollection.isArchived = tagCollection.tags.every(
      (tag) => tag.isArchived,
    );
    tagCollection.isProblematic = tagCollection.tags.some(
      (tag) => tag.isArchived,
    );
  } else if (condition.value === null) {
    tagCollection.tags.push({
      textContent:
        condition.operator === '='
          ? translator(
              'bookkeep.integrations.settings.payableRules.emptySupplier',
            )
          : translator(
              'bookkeep.integrations.settings.payableRules.anySupplier',
            ),
      isArchived: false,
    });
  } else if (condition.value === 'any') {
    return getSupplierTag({
      condition: {
        field: condition.field,
        operator: negateOperator(condition.operator),
        value: null,
      },
      translator,
    });
  }

  return tagCollection;
};

const negateOperator = (operator: '=' | '!='): '=' | '!=' => {
  return operator === '=' ? '!=' : '=';
};

export const generateConditionBox = (
  condition: EnrichedPayableRule['conditions'][number],
  company: Company,
  translator: TGlobalFunctionTyped,
  designatedCustomFieldId?: string,
) => {
  let title: string;
  let tags: ReactNode = null;
  switch (condition.field) {
    case 'supplier': {
      tags = generateSupplierConditionTags(
        condition as EnrichedValueFilter<'supplier'>,
        translator,
      );
      title = translator(
        'bookkeep.integrations.settings.payableRules.details.supplierIs',
      );
      break;
    }
    case 'costCenter': {
      tags = generateCostCenterConditionTags(
        condition as EnrichedValueFilter<'costCenter'>,
        translator,
      );
      title = translator(
        'bookkeep.integrations.settings.payableRules.details.costCenterIs',
      );
      break;
    }
    case 'requestor': {
      tags = generateRequestorConditionTags(
        condition as EnrichedValueFilter<'requestor'>,
        translator,
      );
      title = translator(
        'bookkeep.integrations.settings.payableRules.details.requestorIs',
      );
      break;
    }
    case 'payableType': {
      title = translator(
        'bookkeep.integrations.settings.payableRules.details.payableTypeIs',
      );
      tags = generatePayableTypeTags(condition, translator);
      break;
    }
    case 'grossAmount': {
      title = translator('payables.filterFields.grossAmount');
      tags = (
        <div className="flex flex-wrap gap-4 truncate">
          <Tag variant="white">
            {getGrossAmountTag({
              condition,
              currency: company.currency,
            })}
          </Tag>
        </div>
      );
      break;
    }
    case 'customField': {
      if (
        designatedCustomFieldId &&
        'customField' in condition.value &&
        condition.value.customField.id === designatedCustomFieldId
      ) {
        title = translator(
          'bookkeep.integrations.settings.payableRules.details.expenseCategoryIs',
        );
      } else if ('customField' in condition.value) {
        title = translator(
          'bookkeep.integrations.settings.payableRules.details.customFieldIs',
          {
            customField: condition.value.customField.name,
          },
        );
      } else {
        title = translator('misc.unknown');
      }
      tags = generateCustomFieldConditionTags(
        condition as EnrichedCustomFieldFilter,
        translator,
      );
      break;
    }
    default:
      title = translator('misc.unknown');
  }

  return (
    <Box
      variant="secondary"
      key={`${condition.field}${condition.operator}`}
      className="p-16"
    >
      <div className="flex flex-col gap-4 truncate">
        <span>{title}</span>
        {tags}
      </div>
    </Box>
  );
};

const generateSupplierConditionTags = (
  condition: EnrichedValueFilter<'supplier'>,
  translator: TGlobalFunctionTyped,
) => {
  let tags: ReactNode = null;
  if (Array.isArray(condition.value)) {
    tags = (
      <div className="flex flex-wrap gap-4 truncate">
        {condition.value.map((supplier) => {
          if (typeof supplier === 'string') {
            return (
              <Tag variant="white" key={supplier}>
                {`${condition.operator === '=' ? '' : `≠ `}${supplier}`}
              </Tag>
            );
          }
          const icon: IconName | undefined = supplier.isArchived
            ? 'triangle-warning'
            : undefined;
          const className = supplier.isArchived ? 'crossed-out' : undefined;
          const variant: TagVariant = supplier.isArchived ? 'warning' : 'white';
          return (
            <Tag
              variant={variant}
              className={className}
              iconName={icon}
              key={supplier.id}
            >
              {`${condition.operator === '=' ? '' : `≠ `}${supplier.name}`}
            </Tag>
          );
        })}
      </div>
    );
  } else if (typeof condition.value === 'string') {
    tags = (
      <div className="flex flex-wrap gap-4 truncate">
        <Tag variant="white">{`${condition.operator === '=' ? '' : `≠ `}${condition.value}`}</Tag>
      </div>
    );
  } else {
    tags = (
      <div className="flex flex-wrap gap-4 truncate">
        <Tag variant="white">
          {translator(
            condition.operator === '='
              ? 'bookkeep.integrations.settings.payableRules.emptySupplier'
              : 'bookkeep.integrations.settings.payableRules.anySupplier',
          )}
        </Tag>
      </div>
    );
  }
  return tags;
};

const generateCostCenterConditionTags = (
  condition: EnrichedValueFilter<'costCenter'>,
  translator: TGlobalFunctionTyped,
) => {
  let tags: ReactNode = null;
  if (Array.isArray(condition.value)) {
    tags = (
      <div className="flex flex-wrap gap-4 truncate">
        {condition.value.map((costCenter) => {
          if (typeof costCenter === 'string') {
            return undefined;
          }

          return (
            <Tag
              key={costCenter.id}
              variant={costCenter.isDeleted ? 'warning' : 'white'}
              iconName={costCenter.isDeleted ? 'triangle-warning' : undefined}
              className={costCenter.isDeleted ? 'crossed-out' : undefined}
            >
              {`${condition.operator === '=' ? '' : `≠ `}${costCenter.name}`}
            </Tag>
          );
        })}
      </div>
    );
  } else if (typeof condition.value === 'string') {
    tags = (
      <div className="flex flex-wrap gap-4 truncate">
        <Tag variant="white">
          {`${condition.operator === '=' ? '' : `≠ `}${condition.value}`}
        </Tag>
      </div>
    );
  } else {
    tags = (
      <div className="flex flex-wrap gap-4 truncate">
        <Tag variant="white">
          {translator(
            condition.operator === '='
              ? 'bookkeep.integrations.settings.payableRules.emptyCostCenter'
              : 'bookkeep.integrations.settings.payableRules.anyCostCenter',
          )}
        </Tag>
      </div>
    );
  }
  return tags;
};

const generateRequestorConditionTags = (
  condition: EnrichedValueFilter<'requestor'>,
  translator: TGlobalFunctionTyped,
) => {
  const tagCollection = getRequestorTag({ condition, translator });
  return (
    <div className="flex flex-wrap gap-4 truncate">
      {tagCollection.tags.map((tag) => {
        const icon: IconName | undefined = tag.isArchived
          ? 'triangle-warning'
          : undefined;
        const className = tag.isArchived ? 'crossed-out' : undefined;
        const variant: TagVariant = tag.isArchived ? 'warning' : 'white';
        return (
          <Tag
            variant={variant}
            className={className}
            iconName={icon}
            key={tag.textContent}
          >
            {tag.textContent}
          </Tag>
        );
      })}
    </div>
  );
};

const generatePayableTypeTags = (
  condition: Extract<
    EnrichedPayableRule['conditions'][number],
    { field: 'payableType' }
  >,
  translator: TGlobalFunctionTyped,
) => {
  let tags: ReactNode = null;
  if (Array.isArray(condition.value)) {
    tags = (
      <div className="flex flex-wrap gap-4 truncate">
        {condition.value.map((payableType) => {
          const readableType = getReadablePayableType(payableType);
          return (
            <Tag variant="white" key={payableType}>
              {`${condition.operator === '=' ? '' : `≠ `}${
                readableType
                  ? translator(readableType)
                  : translator('misc.unknown')
              }`}
            </Tag>
          );
        })}
      </div>
    );
  } else if (typeof condition.value === 'string') {
    const readableType = getReadablePayableType(condition.value);
    tags = (
      <div className="flex flex-wrap gap-4 truncate">
        <Tag variant="white">
          {`${condition.operator === '=' ? '' : `≠ `}${
            readableType ? translator(readableType) : translator('misc.unknown')
          }`}
        </Tag>
      </div>
    );
  } else {
    tags = (
      <div className="flex flex-wrap gap-4 truncate">
        <Tag variant="white">
          {translator(
            condition.operator === '='
              ? 'bookkeep.integrations.settings.payableRules.emptyPayableType'
              : 'bookkeep.integrations.settings.payableRules.anyPayableType',
          )}
        </Tag>
      </div>
    );
  }
  return tags;
};

const generateCustomFieldConditionTags = (
  condition: EnrichedCustomFieldFilter,
  translator: TGlobalFunctionTyped,
) => {
  let tags: ReactNode = null;
  if ('customField' in condition.value) {
    const customField = condition.value.customField;
    if (Array.isArray(condition.value.value)) {
      const isCustomFieldArchived = customField.archiveDate !== null;
      tags = (
        <div className="flex flex-wrap gap-4 truncate">
          {condition.value.value.map((customFieldValue) => {
            let icon: IconName | undefined = isCustomFieldArchived
              ? 'triangle-warning'
              : undefined;
            let className = isCustomFieldArchived ? 'crossed-out' : undefined;
            let variant: TagVariant = isCustomFieldArchived
              ? 'warning'
              : 'white';
            if (typeof customFieldValue === 'string') {
              return (
                <Tag
                  variant={variant}
                  key={customFieldValue}
                  className={className}
                  iconName={icon}
                >
                  {`${condition.operator === '=' ? '' : `≠ `}${customFieldValue}`}
                </Tag>
              );
            }
            if (customField.type === 'Boolean') {
              return (
                <Tag
                  variant={variant}
                  key={customFieldValue.id}
                  className={className}
                  iconName={icon}
                >
                  {customFieldValue.name === true
                    ? translator('misc.yes')
                    : translator('misc.no')}
                </Tag>
              );
            }
            if (customFieldValue.archiveDate) {
              icon = 'triangle-warning';
              className = 'crossed-out';
              variant = 'warning';
            }
            return (
              <Tag
                variant={variant}
                key={customFieldValue.id}
                className={className}
                iconName={icon}
              >
                {`${condition.operator === '=' ? '' : `≠ `}${customFieldValue.name}`}
              </Tag>
            );
          })}
        </div>
      );
    }
    if (typeof condition.value.value === 'string') {
      tags = (
        <div className="flex flex-wrap gap-4 truncate">
          <Tag variant="white">{`${condition.operator === '=' ? '' : `≠ `}${condition.value.value}`}</Tag>
        </div>
      );
    }
    if (condition.value.value === null) {
      tags = (
        <div className="flex flex-wrap gap-4 truncate">
          <Tag variant="white">
            {translator(
              condition.operator === '='
                ? 'bookkeep.integrations.settings.payableRules.emptyCustomFieldValue'
                : 'bookkeep.integrations.settings.payableRules.anyCustomFieldValue',
              { customField: condition.value.customField.name },
            )}
          </Tag>
        </div>
      );
    }
  }
  return tags;
};
